import React from 'react';
import { Link } from 'react-router-dom';
import { Tabs, Table, Select, DatePicker, Button, Space, Typography, Statistic, Row, Col, Card, Tag, Tooltip, notification, Progress } from 'antd';
import { ReloadOutlined, DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useCompanyId } from '../../lib/books';

const money = (v) => (v === null || v === undefined ? '' : Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const hrs = (v) => (v === null || v === undefined ? '' : Number(v).toLocaleString('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
const pctCell = (v) => (v === null || v === undefined ? <Typography.Text type="secondary">-</Typography.Text> : `${v}%`);
const signed = (v) => <span style={{ color: v < 0 ? '#cf1322' : v > 0 ? '#389e0d' : undefined }}>{money(v)}</span>;

/** Minimal inline bar chart (no chart library): one or two series per month. */
function Bars({ rows, series, height = 160 }) {
  const max = Math.max(1, ...rows.flatMap((r) => series.map((s) => Math.abs(Number(r[s.key] || 0)))));
  const w = Math.max(320, rows.length * 56);
  const bw = 40 / series.length;
  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width={w} height={height + 34} style={{ display: 'block' }}>
        {rows.map((r, i) => (
          <g key={r.month} transform={`translate(${i * 56 + 8},0)`}>
            {series.map((s, j) => {
              const v = Number(r[s.key] || 0);
              const h = (Math.abs(v) / max) * height;
              return (
                <g key={s.key}>
                  <title>{`${r.label} ${s.label}: ${money(v)}`}</title>
                  <rect x={j * bw} y={height - h} width={bw - 2} height={h} fill={s.color} rx={2} />
                </g>
              );
            })}
            <text x={20} y={height + 16} fontSize={11} textAnchor="middle" fill="#6b7280">{r.label}</text>
          </g>
        ))}
      </svg>
      <Space size={12} style={{ fontSize: 12 }}>
        {series.map((s) => (
          <span key={s.key}><span style={{ display: 'inline-block', width: 10, height: 10, background: s.color, marginRight: 4, borderRadius: 2 }} />{s.label}</span>
        ))}
      </Space>
    </div>
  );
}

/** Downloads the current table as CSV (no server round-trip). */
function exportCsv(name, columns, rows) {
  const cols = columns.filter((c) => c.dataIndex);
  const esc = (v) => `"${String(v === null || v === undefined ? '' : v).replace(/"/g, '""')}"`;
  const lines = [cols.map((c) => esc(c.title)).join(','), ...rows.map((r) => cols.map((c) => esc(r[c.dataIndex])).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${name}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** Performance Analysis (xlsx "Reports" sheet): the firm's utilisation, client profitability, service lines and sales. */
export default function PerformancePage() {
  const auth = useAuth();
  const isSystemAdmin = auth.user.role === 'SYSTEM_ADMIN';
  const { companyId, setCompanyId } = useCompanyId();
  const [companies, setCompanies] = React.useState([]);
  const [range, setRange] = React.useState([moment().startOf('year'), moment()]);
  const [tab, setTab] = React.useState('overview');
  const [data, setData] = React.useState({});
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (isSystemAdmin) {
      request.get('/companies').then((r) => r.json()).then((r) => {
        if (r.status) {
          setCompanies(r.data);
          setCompanyId((c) => c || (r.data.length ? r.data[0].id : null));
        }
      });
    }
  }, [isSystemAdmin, setCompanyId]);

  const load = React.useCallback(() => {
    if (!companyId) return;
    const q = { company_id: companyId, from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') };
    const key = tab === 'overview' ? 'summary' : tab;
    setLoading(true);
    const calls = key === 'summary' ? [request.get('/reports/performance/summary', q), request.get('/reports/performance/sales', q)] : [request.get(`/reports/performance/${key}`, q)];
    Promise.all(calls.map((p) => p.then((r) => r.json())))
      .then((results) => {
        results.forEach((r, i) => {
          if (!r.status) return notification.error({ message: r.message });
          setData((d) => ({ ...d, [key === 'summary' && i === 1 ? 'sales' : key]: r.data }));
        });
      })
      .finally(() => setLoading(false));
  }, [companyId, range, tab]);
  React.useEffect(load, [load]);

  const s = data.summary;
  const staff = data.staff;
  const clients = data.clients;
  const services = data.services;
  const sales = data.sales;

  const staffColumns = [
    { title: 'Staff', dataIndex: 'name', render: (v, r) => <Link to={`/users/${r.user_id}`}>{v}</Link> },
    { title: 'Level', dataIndex: 'designation', render: (v) => v || '-' },
    { title: 'Hours', dataIndex: 'hours', align: 'right', render: hrs },
    { title: 'Billable', dataIndex: 'billable_hours', align: 'right', render: hrs },
    { title: 'Non-billable', dataIndex: 'non_billable_hours', align: 'right', render: hrs },
    { title: 'Billable %', dataIndex: 'billable_pct', align: 'right', render: pctCell },
    { title: 'Expected', dataIndex: 'expected_hours', align: 'right', render: hrs },
    { title: 'Utilisation', dataIndex: 'utilisation_pct', width: 150, render: (v) => (v === null ? '-' : <Progress percent={Math.min(100, v)} size="small" format={() => `${v}%`} status={v >= 70 ? 'success' : v >= 50 ? 'normal' : 'exception'} />) },
    { title: 'Draft hrs', dataIndex: 'draft_hours', align: 'right', render: hrs },
    { title: 'Billed hrs', dataIndex: 'billed_hours', align: 'right', render: hrs },
    { title: 'Time cost', dataIndex: 'cost', align: 'right', render: money },
    { title: 'WIP value', dataIndex: 'wip_value', align: 'right', render: money },
    { title: 'Clients', dataIndex: 'clients', align: 'right' },
  ];
  const clientColumns = [
    { title: 'Client', dataIndex: 'client_name', render: (v, r) => (r.client_id ? <Link to={`/clients/${r.client_id}`}>{v}</Link> : <Typography.Text type="secondary">{v}</Typography.Text>) },
    { title: 'Code', dataIndex: 'client_code', width: 80, render: (v) => v || '-' },
    { title: 'Associate', dataIndex: 'associate', render: (v) => v || '-' },
    { title: 'Hours', dataIndex: 'hours', align: 'right', render: hrs },
    { title: 'Time cost', dataIndex: 'cost', align: 'right', render: money },
    { title: 'Invoiced (net)', dataIndex: 'invoiced_net', align: 'right', render: money },
    { title: 'Credit notes', dataIndex: 'credit_notes_net', align: 'right', render: money },
    { title: 'Net fees', dataIndex: 'net_fees', align: 'right', render: (v) => <strong>{money(v)}</strong> },
    { title: 'Margin', dataIndex: 'margin', align: 'right', render: signed },
    { title: 'Realisation', dataIndex: 'realisation_pct', align: 'right', render: pctCell },
    { title: 'Rate/hr', dataIndex: 'effective_rate', align: 'right', render: money },
    { title: 'WIP', dataIndex: 'wip_value', align: 'right', render: money },
    { title: 'Receipts', dataIndex: 'receipts', align: 'right', render: money },
    { title: 'Outstanding', dataIndex: 'outstanding', align: 'right', render: money },
    { title: 'Overdue', dataIndex: 'overdue', align: 'right', render: (v) => <span style={{ color: v ? '#cf1322' : undefined }}>{money(v)}</span> },
  ];
  const serviceColumns = [
    { title: 'Service', dataIndex: 'service_name', render: (v, r) => (r.mnemonic ? <span><Tag>{r.mnemonic}</Tag>{v}</span> : v) },
    { title: 'Active engagements', dataIndex: 'active_engagements', align: 'right' },
    { title: 'Clients', dataIndex: 'clients', align: 'right' },
    { title: 'People', dataIndex: 'people', align: 'right' },
    { title: 'Hours', dataIndex: 'hours', align: 'right', render: hrs },
    { title: 'Time cost', dataIndex: 'cost', align: 'right', render: money },
    { title: 'Net fees', dataIndex: 'net_fees', align: 'right', render: (v) => <strong>{money(v)}</strong> },
    { title: 'Margin', dataIndex: 'margin', align: 'right', render: signed },
    { title: 'Realisation', dataIndex: 'realisation_pct', align: 'right', render: pctCell },
    { title: 'Rate/hr', dataIndex: 'effective_rate', align: 'right', render: money },
  ];
  const salesColumns = [
    { title: 'Month', dataIndex: 'label', width: 90 },
    { title: 'Invoices', dataIndex: 'invoices', align: 'right' },
    { title: 'Invoiced (net)', dataIndex: 'invoiced_net', align: 'right', render: money },
    { title: 'Credit notes', dataIndex: 'credit_notes_net', align: 'right', render: money },
    { title: 'Net fees', dataIndex: 'net_fees', align: 'right', render: (v) => <strong>{money(v)}</strong> },
    { title: 'VAT', dataIndex: 'vat', align: 'right', render: money },
    { title: 'Gross', dataIndex: 'invoiced_total', align: 'right', render: money },
    { title: 'Receipts', dataIndex: 'receipts', align: 'right', render: money },
    { title: 'Hours', dataIndex: 'hours', align: 'right', render: hrs },
    { title: 'Time cost', dataIndex: 'cost', align: 'right', render: money },
    { title: 'Margin', dataIndex: 'margin', align: 'right', render: signed },
  ];
  const totalsRow = (columns, totals, label = 'Total') =>
    totals && (
      <Table.Summary.Row>
        {columns.map((c, i) => (
          <Table.Summary.Cell key={c.dataIndex || i} index={i} align={c.align}>
            <strong>{i === 0 ? label : totals[c.dataIndex] !== undefined && totals[c.dataIndex] !== null ? (c.render ? c.render(totals[c.dataIndex], totals) : totals[c.dataIndex]) : ''}</strong>
          </Table.Summary.Cell>
        ))}
      </Table.Summary.Row>
    );
  const tableProps = (columns, rows, totals, name) => ({
    size: 'small',
    rowKey: (r, i) => r.user_id || r.client_id || r.service_id || r.month || i,
    pagination: false,
    loading,
    dataSource: rows,
    columns,
    scroll: { x: 1300 },
    summary: () => totalsRow(columns, totals),
    title: () => (
      <Space className="no-print">
        <Button size="small" icon={<DownloadOutlined />} onClick={() => exportCsv(name, columns, rows)}>Export CSV</Button>
      </Space>
    ),
  });
  const tile = (title, value, extra, color) => (
    <Col xs={12} md={8} xl={4}>
      <Card size="small"><Statistic title={title} value={value} valueStyle={color ? { color } : undefined} /> {extra && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{extra}</Typography.Text>}</Card>
    </Col>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 16 }} className="no-print">
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Performance Analysis
          </Typography.Title>
          <Typography.Text type="secondary">How the firm performs over the period: staff utilisation, client profitability, service lines and sales. Time cost = hours x the career-level rate at the time; fees are net of VAT.</Typography.Text>
        </div>
        <Space wrap>
          {isSystemAdmin && <Select style={{ width: 180 }} value={companyId} onChange={setCompanyId} options={companies.map((c) => ({ value: c.id, label: c.name }))} />}
          <DatePicker.RangePicker value={range} onChange={(v) => v && setRange(v)} allowClear={false} ranges={{
            'This month': [moment().startOf('month'), moment()],
            'Last month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
            'This quarter': [moment().startOf('quarter'), moment()],
            'Year to date': [moment().startOf('year'), moment()],
            'Last year': [moment().subtract(1, 'year').startOf('year'), moment().subtract(1, 'year').endOf('year')],
          }} />
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          <Button icon={<PrinterOutlined />} onClick={() => window.print()}>Print</Button>
        </Space>
      </div>
      <style>{`@media print { .ant-layout-sider, .ant-layout-header, .ant-layout-footer, .no-print, .ant-tabs-nav { display: none !important; } }`}</style>

      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'overview',
            label: 'Overview',
            children: s && (
              <div>
                <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                  {tile('Hours logged', hrs(s.time.hours), `${s.time.people} people, ${s.time.working_days} working days`)}
                  {tile('Billable share', s.time.billable_pct === null ? '-' : `${s.time.billable_pct}%`, `${hrs(s.time.billable_hours)} billable hours`)}
                  {tile('Utilisation', s.time.utilisation_pct === null ? '-' : `${s.time.utilisation_pct}%`, `of ${hrs(s.time.expected_hours_per_person)} expected hours per person`, s.time.utilisation_pct >= 70 ? '#389e0d' : undefined)}
                  {tile('Net fees', money(s.billing.net_fees), `${s.billing.invoices} invoices, ${money(s.billing.credit_notes_net)} credited`)}
                  {tile('Realisation', s.billing.realisation_pct === null ? '-' : `${s.billing.realisation_pct}%`, `margin ${money(s.billing.margin)} on time cost ${money(s.time.cost)}`, s.billing.margin >= 0 ? '#389e0d' : '#cf1322')}
                  {tile('WIP (unbilled)', money(s.time.wip_value), `${hrs(s.time.wip_hours)} approved billable hours`)}
                  {tile('Receipts', money(s.billing.receipts), `${s.billing.receipt_count} receipts`)}
                  {tile('Outstanding today', money(s.billing.outstanding), `${s.billing.open_invoices} open invoices`)}
                  {tile('Overdue today', money(s.billing.overdue), 'past due date', s.billing.overdue > 0 ? '#cf1322' : undefined)}
                  {tile('Active clients', s.workload.active_clients, `${s.workload.active_engagements} active engagements`)}
                  {tile('Open tasks', s.workload.open_tasks, `${s.workload.overdue_tasks} overdue`, s.workload.overdue_tasks > 0 ? '#d46b08' : undefined)}
                  {tile('Pending deadlines', s.workload.pending_deadlines, `${s.workload.overdue_deadlines} overdue`, s.workload.overdue_deadlines > 0 ? '#cf1322' : undefined)}
                </Row>
                {sales && (
                  <Row gutter={16}>
                    <Col xs={24} xl={14}>
                      <Card size="small" title="Fees, receipts and time cost by month">
                        <Bars rows={sales.rows} series={[{ key: 'net_fees', label: 'Net fees', color: '#5b6cff' }, { key: 'receipts', label: 'Receipts', color: '#52c41a' }, { key: 'cost', label: 'Time cost', color: '#d9d9d9' }]} />
                      </Card>
                    </Col>
                    <Col xs={24} xl={10}>
                      <Card size="small" title="Top clients by fees">
                        <Table size="small" pagination={false} rowKey="client_name" dataSource={sales.top_clients} columns={[{ title: 'Client', dataIndex: 'client_name' }, { title: 'Invoices', dataIndex: 'invoices', align: 'right' }, { title: 'Net', dataIndex: 'net', align: 'right', render: money }]} locale={{ emptyText: 'No invoices in the period' }} />
                      </Card>
                    </Col>
                  </Row>
                )}
              </div>
            ),
          },
          {
            key: 'staff',
            label: 'Staff utilisation',
            children: staff && (
              <div>
                <Typography.Text type="secondary">
                  Expected hours per person: {hrs(staff.expected.hours)} ({staff.expected.working_days} working days x {staff.expected.hours_per_day} h, holidays excluded). Utilisation = billable hours / expected hours. <Tooltip title="Set working days, hours and holidays on the company's Settings tab"><Link to="/companies">Calendar setup</Link></Tooltip>
                </Typography.Text>
                <Table {...tableProps(staffColumns, staff.rows, staff.totals, 'staff-utilisation')} />
              </div>
            ),
          },
          {
            key: 'clients',
            label: 'Client profitability',
            children: clients && (
              <div>
                <Typography.Text type="secondary">Net fees = invoices issued in the period less credit notes. Realisation = net fees / time cost. Outstanding and overdue are as at today, regardless of period.</Typography.Text>
                <Table {...tableProps(clientColumns, clients.rows, clients.totals, 'client-profitability')} />
              </div>
            ),
          },
          {
            key: 'services',
            label: 'Service lines',
            children: services && (
              <div>
                <Typography.Text type="secondary">Fees are taken from invoice lines tagged with the service; time from timesheets tagged with it.</Typography.Text>
                <Table {...tableProps(serviceColumns, services.rows, services.totals, 'service-lines')} />
              </div>
            ),
          },
          {
            key: 'sales',
            label: 'Sales by month',
            children: sales && (
              <div>
                <Card size="small" style={{ marginBottom: 12 }}>
                  <Bars rows={sales.rows} series={[{ key: 'net_fees', label: 'Net fees', color: '#5b6cff' }, { key: 'receipts', label: 'Receipts', color: '#52c41a' }]} height={120} />
                </Card>
                <Table {...tableProps(salesColumns, sales.rows, sales.totals, 'sales-by-month')} />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
